import { usePeriod } from '@/app/context/PeriodContext';
import { SharingDebitProjectResponse } from '@/app/models/sharingDetails';
import { useExportFilesMutation, usePeriodFileByPeriodQuery } from '@/app/services/periodFile';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import IosShareIcon from '@mui/icons-material/IosShare';
import { styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ActionMenu, { ItemAction } from '../common/ActionMenu';

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 'bold',
  padding: theme.spacing(1),
}));

const RowTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 13,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.primary.main,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease-in-out',
  '&:hover, tr:hover &': {
    backgroundColor: '#FFFFFF',
  },
}));

type RuleClassificationTableGridParams = {
  rows: SharingDebitProjectResponse[];
};

function RuleClassificationTable(params: RuleClassificationTableGridParams) {
  const { t } = useTranslation('sharingDetails');
  const navigate = useNavigate();
  const { selectedPeriod } = usePeriod();

  const HandleNavigate = (code: string) => navigate(`/sharingDetail/${code}`);

  const { refetch } = usePeriodFileByPeriodQuery(selectedPeriod?.id as number, {
    skip: !selectedPeriod,
  });

  const [exportFiles] = useExportFilesMutation();

  const handleMenuOpen = async (projectCode: string) => {
    await toast.promise(
      exportFiles({
        date: selectedPeriod?.date as string,
        projectCode: projectCode as string,
      }).unwrap(),
      {
        pending: t('sharing.sent') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );

    // eslint-disable-next-line no-promise-executor-return
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const result = await refetch().unwrap();
    const file = result.files?.find((f) => f.fileName.includes(projectCode));
    if (file?.filePath) {
      const link = document.createElement('a');
      link.href = file.filePath;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const { rows } = params;
  return (
    <TableContainer sx={{ marginLeft: 0, padding: 0, width: '100%' }}>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow>
            <HeaderTableCell>{t('project')}</HeaderTableCell>
            <HeaderTableCell>{t('total.paid')}</HeaderTableCell>
            <HeaderTableCell align="right">{t('actions')}</HeaderTableCell>
          </TableRow>
        </TableHead>
      </Table>
      <Table
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          fontSize: '16px',
        }}
      >
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.debitProjectCode + row.debitProjectName}>
              <RowTableCell
                onClick={() => HandleNavigate(row.debitProjectCode)}
              >{`${row.debitProjectCode} - ${row.debitProjectName}`}</RowTableCell>
              <RowTableCell onClick={() => HandleNavigate(row.debitProjectCode)}>
                {formatterMonetaryValue(row.value)}
              </RowTableCell>
              <RowTableCell align="right">
                <ActionMenu>
                  <ItemAction
                    text={t('export')}
                    Icon={IosShareIcon}
                    onClick={() => handleMenuOpen(row.debitProjectCode)}
                  />
                </ActionMenu>
              </RowTableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell sx={{ textAlign: 'center' }} colSpan={5}>
                <Typography variant="subtitle2">{t('no.data.found')}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default RuleClassificationTable;
